import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Container } from './ui/Container'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'
import { toast } from 'sonner'
import {
  Apple,
  Calculator,
  Target,
  TrendingUp,
  Clock,
  Heart,
  Leaf,
  Zap,
  ChevronRight,
  Plus,
  CheckCircle
} from 'lucide-react'

interface NutritionGoal {
  id: string
  name: string
  description: string
  target: number
  current: number
  unit: string
  icon: React.ReactNode
  color: string
  recommendations: string[]
}

interface Ingredient {
  name: string
  amount: number
  unit: string
}

interface MealPlan {
  id: string
  name: string
  calories: number
  protein: number
  carbs: number
  fat: number
  prepTime: number
  difficulty: 'easy' | 'medium' | 'hard'
  tags: string[]
  image: string
  ingredients: Ingredient[]
  instructions: string[]
}

const nutritionGoals: NutritionGoal[] = [
  {
    id: '1',
    name: 'Daily Calories',
    description: 'Maintain optimal energy balance',
    target: 2000,
    current: 1850,
    unit: 'kcal',
    icon: <Zap className="w-5 h-5" />,
    color: 'from-yellow-500 to-orange-500',
    recommendations: ['Increase healthy fats', 'Add more protein-rich snacks']
  },
  {
    id: '2',
    name: 'Protein Intake',
    description: 'Support muscle recovery and growth',
    target: 120,
    current: 95,
    unit: 'g',
    icon: <Target className="w-5 h-5" />,
    color: 'from-blue-500 to-cyan-500',
    recommendations: ['Add Greek yogurt', 'Include lean meats', 'Try protein smoothies']
  },
  {
    id: '3',
    name: 'Fiber Intake',
    description: 'Support digestive health',
    target: 25,
    current: 18,
    unit: 'g',
    icon: <Leaf className="w-5 h-5" />,
    color: 'from-green-500 to-emerald-500',
    recommendations: ['Add more vegetables', 'Include whole grains', 'Eat more legumes']
  },
  {
    id: '4',
    name: 'Water Intake',
    description: 'Maintain proper hydration',
    target: 8,
    current: 6,
    unit: 'glasses',
    icon: <Heart className="w-5 h-5" />,
    color: 'from-blue-500 to-indigo-500',
    recommendations: ['Carry water bottle', 'Set reminders', 'Add lemon for flavor']
  }
]

const mealPlans: MealPlan[] = [
  {
    id: '1',
    name: 'Artisanal Quinoa Bowl',
    calories: 450,
    protein: 25,
    carbs: 35,
    fat: 18,
    prepTime: 15,
    difficulty: 'easy',
    tags: ['organic-produce', 'vegan-excellence', 'bio-synced'],
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=800',
    ingredients: [
      { name: 'Red Quinoa', amount: 120, unit: 'g' },
      { name: 'Organic Chickpeas', amount: 80, unit: 'g' },
      { name: 'English Cucumber', amount: 60, unit: 'g' },
      { name: 'Cherry Tomatoes', amount: 50, unit: 'g' },
      { name: 'Greek Feta Cheese', amount: 30, unit: 'g' },
      { name: 'Extra Virgin Olive Oil', amount: 15, unit: 'ml' },
      { name: 'Fresh Lemon Juice', amount: 10, unit: 'ml' }
    ],
    instructions: ['Cook quinoa', 'Chop vegetables', 'Mix ingredients', 'Add dressing', 'Serve fresh']
  },
  {
    id: '2',
    name: 'High-Altitude Protein Harvest',
    calories: 380,
    protein: 35,
    carbs: 20,
    fat: 22,
    prepTime: 20,
    difficulty: 'medium',
    tags: ['carefully-picked', 'regenerative-farming', 'muscle-purity'],
    image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=800',
    ingredients: [
      { name: 'Pasture-Raised Chicken', amount: 150, unit: 'g' },
      { name: 'Baby Spinach & Arugula', amount: 40, unit: 'g' },
      { name: 'Hass Avocado', amount: 60, unit: 'g' },
      { name: 'Toasted Almonds', amount: 15, unit: 'g' },
      { name: 'Cherry Tomatoes', amount: 40, unit: 'g' },
      { name: 'Balsamic Vinaigrette', amount: 20, unit: 'ml' }
    ],
    instructions: ['Grill chicken', 'Wash greens', 'Slice avocado', 'Toast almonds', 'Combine and dress']
  },
  {
    id: '3',
    name: 'Metabolic Elixir Smoothie',
    calories: 320,
    protein: 15,
    carbs: 45,
    fat: 12,
    prepTime: 5,
    difficulty: 'easy',
    tags: ['antioxidant-gold', 'vegan-organic', 'cellular-repair'],
    image: 'https://images.unsplash.com/photo-1623855244697-5d8fbe9c7992?auto=format&fit=crop&q=80&w=800',
    ingredients: [
      { name: 'Mixed Wild Berries', amount: 100, unit: 'g' },
      { name: 'Baby Spinach', amount: 30, unit: 'g' },
      { name: 'Fresh Ginger Root', amount: 5, unit: 'g' },
      { name: 'Turmeric Powder', amount: 1, unit: 'g' },
      { name: 'Greek Yogurt (Organic)', amount: 120, unit: 'g' },
      { name: 'Unsweetened Almond Milk', amount: 200, unit: 'ml' },
      { name: 'Organic Chia Seeds', amount: 10, unit: 'g' }
    ],
    instructions: ['Add all ingredients to blender', 'Blend until smooth', 'Serve immediately', 'Enjoy!']
  }
]

const NutritionSection: React.FC = () => {
  const [selectedGoal, setSelectedGoal] = useState<string | null>(null)
  const [selectedMeal, setSelectedMeal] = useState<MealPlan | null>(null)

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  const getProgressPercentage = (current: number, target: number) => {
    return Math.min((current / target) * 100, 100)
  }

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      easy: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      hard: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }
    return colors[difficulty as keyof typeof colors]
  }

  return (
    <section className="py-20 bg-theme-bg content-section">
      <Container>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6">
              <Apple className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-6">
              Precision Bio-Nutrition
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto leading-relaxed">
              Curating the biology of performance through artisanal nutrition. Our AI audits every ingredient for organic purity and metabolic compatibility.
            </p>
          </motion.div>
        </div>

        {/* Nutrition Goals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-8 text-center">Daily Nutrition Goals</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {nutritionGoals.map((goal, index) => {
              const progress = getProgressPercentage(goal.current, goal.target)
              const isSelected = selectedGoal === goal.id
              
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card 
                    className={`h-full flex flex-col overflow-hidden cursor-pointer transition-all duration-300 bg-theme-surface/70 border-2 rounded-xl group ${
                      isSelected 
                        ? 'border-blue-500 shadow-lg' 
                        : 'border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500'
                    }`}
                    onClick={() => setSelectedGoal(isSelected ? null : goal.id)}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center text-white`}>
                          {goal.icon}
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-theme-primary">{goal.current}</div>
                          <div className="text-sm text-theme-secondary">/ {goal.target} {goal.unit}</div>
                        </div>
                      </div>
                      <CardTitle className="text-lg text-theme-primary group-hover:text-blue-400">{goal.name}</CardTitle>
                      <p className="text-sm text-theme-secondary group-hover:text-blue-400 leading-relaxed">{goal.description}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-theme-secondary">Progress</span>
                          <span className="font-semibold text-theme-primary">{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full bg-theme-surface/60 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-teal-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                      {isSelected && (
                        <div className="space-y-2">
                          <h4 className="font-semibold text-theme-primary text-sm">Artisanal Guidance:</h4>
                          {goal.recommendations.map((rec, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-theme-secondary">{rec}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Meal Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-8 text-center">Biologically Optimized Menu</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {mealPlans.map((meal, index) => (
              <motion.div
                key={meal.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer bg-theme-surface/70 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 rounded-xl group">
                  <CardHeader className="pb-4">
                    <div className="mb-4">
                      <img 
                        src={meal.image} 
                        alt={meal.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg text-theme-primary group-hover:text-blue-400">{meal.name}</CardTitle>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(meal.difficulty)}`}>
                        {meal.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-theme-secondary group-hover:text-blue-400">
                      <Clock className="w-4 h-4" />
                      <span>{meal.prepTime} min</span>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                        <div className="text-lg font-bold text-theme-primary">{meal.calories}</div>
                        <div className="text-xs text-theme-secondary">calories</div>
                      </div>
                      <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                        <div className="text-lg font-bold text-theme-primary">{meal.protein}g</div>
                        <div className="text-xs text-theme-secondary">protein</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {meal.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-theme-primary/10 text-theme-primary border border-theme-primary/20 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      className="w-full mt-auto bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700"
                      onClick={() => setSelectedMeal(meal)}
                    >
                      View Recipe
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Meal Detail Modal */}
        {selectedMeal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMeal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-theme-surface/90 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-blue-200 dark:border-blue-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-theme-primary">{selectedMeal.name}</h3>
                  <div className="flex items-center space-x-4 text-sm text-theme-secondary">
                    <span className={`px-2 py-1 rounded-full ${getDifficultyColor(selectedMeal.difficulty)}`}>
                      {selectedMeal.difficulty}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{selectedMeal.prepTime} min</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMeal(null)}
                  className="text-theme-secondary hover:text-theme-text text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-6">
                <img 
                  src={selectedMeal.image} 
                  alt={selectedMeal.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                  <div className="text-lg font-bold text-theme-primary">{selectedMeal.calories}</div>
                  <div className="text-xs text-theme-secondary">calories</div>
                </div>
                <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                  <div className="text-lg font-bold text-theme-primary">{selectedMeal.protein}g</div>
                  <div className="text-xs text-theme-secondary">protein</div>
                </div>
                <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                  <div className="text-lg font-bold text-theme-primary">{selectedMeal.carbs}g</div>
                  <div className="text-xs text-theme-secondary">carbs</div>
                </div>
                <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                  <div className="text-lg font-bold text-theme-primary">{selectedMeal.fat}g</div>
                  <div className="text-xs text-theme-secondary">fat</div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                    <Apple className="w-4 h-4 mr-2 text-green-500" />
                    Ingredients
                  </h4>
                  <ul className="space-y-1">
                    {selectedMeal.ingredients.map((ingredient, idx) => (
                      <li key={idx} className="flex items-center justify-between text-sm text-theme-secondary">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span>{ingredient.name}</span>
                        </div>
                        <span className="font-mono font-bold text-theme-primary">{ingredient.amount}{ingredient.unit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                    <Calculator className="w-4 h-4 mr-2 text-blue-500" />
                    Instructions
                  </h4>
                  <ol className="space-y-2">
                    {selectedMeal.instructions.map((instruction, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-theme-secondary">
                        <span className="bg-primary-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mt-0.5">
                          {idx + 1}
                        </span>
                        <span>{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="flex space-x-4 mt-8">
                <Button className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Meal Plan
                </Button>
                <Button variant="outline" className="flex-1">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Track Nutrition
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center bg-theme-surface/80 border-2 border-blue-200 dark:border-blue-700 rounded-2xl p-8 text-theme-text"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center">
            <Apple className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-3xl font-bold mb-4 text-theme-primary">Executive Nutrition Archtecture</h3>
          <p className="text-xl mb-8 text-theme-secondary max-w-2xl mx-auto">
            Experience the pinnacle of culinary science. Personalized, vegan-forward, and organic-first protocols designed for the high-performance lifestyle.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-blue-600 to-teal-500 text-white hover:from-blue-700 hover:to-teal-600"
            onClick={() => handleComingSoon('Nutrition Plan')}
          >
            Start Nutrition Plan
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}

export default NutritionSection
